import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Archive,
  Box,
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  DoorOpen,
  Home,
  Inbox,
  type LucideIcon,
  Pencil,
  Plus,
  Shapes,
  ShelvingUnit,
  ShoppingBasket,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { locations as locApi } from '@/api/endpoints';
import type { LocationNode, LocationType, LocationView } from '@/types/api';
import { getStoredDefaultHomeId, setStoredDefaultHomeId } from '@/lib/defaultHome';
import { AuthedImage } from '@/components/AuthedImage';
import { NewHomeDialog } from '@/components/NewHomeDialog';
import { PhotoLightbox } from '@/components/PhotoLightbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from '@/components/ui/link';

// Exported for reuse by LocationQuickCreateDialog, which offers the same set of
// location types without duplicating this list. HOME is deliberately excluded —
// per issue #104, creating a HOME (a parentless/root location) is only possible
// via the dedicated "+ Yeni Ev" flow (see NewHomeDialog), never from a generic
// type picker.
export const TYPES: LocationType[] = ['ROOM', 'CABINET', 'SHELF', 'DRAWER', 'BASKET', 'BOX', 'OTHER'];

// One glyph per LocationType so the tree isn't just plain text — see issue #73.
// Chosen for visual distinctiveness rather than a 1:1 real-world match (lucide
// has no dedicated "cabinet"/"drawer" icon).
const TYPE_ICONS: Record<LocationType, LucideIcon> = {
  HOME: Home,
  ROOM: DoorOpen,
  CABINET: Archive,
  SHELF: ShelvingUnit,
  DRAWER: Inbox,
  BASKET: ShoppingBasket,
  BOX: Box,
  OTHER: Shapes,
};

interface FormState {
  name: string;
  type: LocationType;
  description: string;
  parentId: number | null;
}
const EMPTY: FormState = { name: '', type: 'ROOM', description: '', parentId: null };

/**
 * Picks which home (root location) should be considered "selected" whenever
 * the location tree (re)loads — see issue #100. Priority: keep the current
 * selection if it's still a valid root, otherwise the persisted default
 * home, otherwise the first root; null if there are no roots at all (e.g.
 * every location was just deleted). A plain function so this priority logic
 * is unit-testable directly, without depending on effect/query timing.
 */
export function pickSelectedHomeId(
  roots: LocationNode[],
  currentId: number | null,
  storedDefaultId: number | null,
): number | null {
  if (roots.length === 0) return null;
  if (currentId !== null && roots.some((n) => n.id === currentId)) return currentId;
  if (storedDefaultId !== null && roots.some((n) => n.id === storedDefaultId)) return storedDefaultId;
  return roots[0].id;
}

/**
 * Every node id in `node`'s own subtree (itself + all descendants, at any
 * depth) — used to expand/collapse a whole home's tree in one shot. See
 * issue #101.
 */
function collectSubtreeIds(node: LocationNode): number[] {
  return [node.id, ...node.children.flatMap(collectSubtreeIds)];
}

export function LocationsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const tree = useQuery({ queryKey: ['locations', 'tree'], queryFn: locApi.tree });

  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [zoomedCoverPhoto, setZoomedCoverPhoto] = useState<string | null>(null);

  // Which home (root location) is currently shown below the strip — see issue
  // #100. Selecting a home also persists it as the device-local default (see
  // selectHome below), so it's auto-reselected on next load.
  const [selectedHomeId, setSelectedHomeId] = useState<number | null>(null);

  // Controls the "+ Yeni Ev"/"Ev Ekle" dialog (see NewHomeDialog) — the only
  // way to create a parentless (root) location as of issue #104.
  const [newHomeOpen, setNewHomeOpen] = useState(false);

  // Auto-select a home once the tree loads/changes — see pickSelectedHomeId.
  useEffect(() => {
    if (!tree.data) return;
    const roots = tree.data;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reconciles the selection with the loaded tree
    setSelectedHomeId((current) => pickSelectedHomeId(roots, current, getStoredDefaultHomeId()));
  }, [tree.data]);

  // Selecting a home in the strip both shows it below and remembers it as the
  // device-local default for next load — see issue #126: a separate "set
  // default" radio was removed since clicking a card already does this job.
  const selectHome = (id: number) => {
    setSelectedHomeId(id);
    setStoredDefaultHomeId(id);
  };

  const editing = useQuery({
    queryKey: ['locations', 'byId', editingId],
    queryFn: () => locApi.get(editingId as number),
    enabled: editingId !== null,
  });

  useEffect(() => {
    if (editing.data) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- seeds the editable form from the fetched location
      setForm({
        name: editing.data.name,
        type: editing.data.type,
        description: editing.data.description ?? '',
        parentId: editing.data.parentId,
      });
    }
  }, [editing.data]);

  const flatNames = useMemo(() => {
    const out: Record<number, string> = {};
    const walk = (nodes: LocationNode[], prefix: string) => {
      for (const n of nodes) {
        out[n.id] = prefix + n.name;
        walk(n.children, prefix + n.name + ' / ');
      }
    };
    walk(tree.data ?? [], '');
    return out;
  }, [tree.data]);

  const selectedHome = useMemo(
    () => (tree.data ?? []).find((n) => n.id === selectedHomeId) ?? null,
    [tree.data, selectedHomeId],
  );

  // Tree rows' expand/collapse state, lifted here (issue #101) instead of each
  // TreeRow owning a private useState — needed so the "expand/collapse all"
  // button below can flip every level at once. Tracks *collapsed* ids rather
  // than expanded ones so a node defaults to open (matching the old per-row
  // useState(true) default) the moment it first appears, with no extra effect
  // needed to seed newly created/loaded nodes into the set.
  const [collapsedIds, setCollapsedIds] = useState<Set<number>>(new Set());

  const toggleRow = (id: number) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const subtreeIds = useMemo(
    () => (selectedHome ? collectSubtreeIds(selectedHome) : []),
    [selectedHome],
  );
  const isSubtreeFullyExpanded = subtreeIds.every((id) => !collapsedIds.has(id));

  const toggleAllRows = () => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (isSubtreeFullyExpanded) {
        subtreeIds.forEach((id) => next.add(id));
      } else {
        subtreeIds.forEach((id) => next.delete(id));
      }
      return next;
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  const create = useMutation({
    mutationFn: () => locApi.create({ name: form.name, type: form.type, description: form.description || null, parentId: form.parentId }),
    onSuccess: () => {
      setForm(EMPTY);
      void qc.invalidateQueries({ queryKey: ['locations'] });
      // Dashboard/Items empty-state CTAs key off totalLocations from ['stats'],
      // which otherwise won't refresh until its own staleTime elapses.
      void qc.invalidateQueries({ queryKey: ['stats'] });
    },
  });

  const update = useMutation({
    mutationFn: async () => {
      const id = editingId as number;
      await locApi.update(id, { name: form.name, type: form.type, description: form.description || null, sortOrder: 0 });
      const originalParent = editing.data?.parentId ?? null;
      if (form.parentId !== originalParent) {
        await locApi.move(id, form.parentId);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['locations'] });
      cancelEdit();
    },
  });

  // Invalidating the ['locations'] prefix (rather than the two keys individually)
  // covers both the tree (whose rows/room-card-strip show the thumbnail) and this
  // location's own ['locations', 'byId', editingId] query in one go — same pattern
  // as the create/update/remove mutations above.
  const uploadCoverPhoto = useMutation({
    mutationFn: (file: File) => locApi.uploadPhoto(editingId as number, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['locations'] }),
  });

  const removeCoverPhoto = useMutation({
    mutationFn: () => locApi.removePhoto(editingId as number),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['locations'] }),
  });

  const descendantsOf = (id: number, nodes: LocationNode[]): Set<number> => {
    const found = new Set<number>();
    const search = (arr: LocationNode[], collect: boolean): void => {
      for (const n of arr) {
        if (collect) found.add(n.id);
        if (n.id === id) search(n.children, true);
        else search(n.children, collect);
      }
    };
    search(nodes, false);
    return found;
  };
  // descendantsOf is intentionally left out of the deps: it is redefined on every
  // render and only reads what is already listed.
  const forbiddenParents = useMemo(
    () => (editingId == null || !tree.data
      ? new Set<number>()
      : new Set([editingId, ...descendantsOf(editingId, tree.data)])),
    [editingId, tree.data],
  );

  const remove = useMutation({
    mutationFn: (id: number) => locApi.remove(id),
    onSuccess: (_data, id) => {
      if (editingId === id) cancelEdit();
      void qc.invalidateQueries({ queryKey: ['locations'] });
      // See the create mutation above: deleting a location can also bring the
      // account back down to zero, which the Dashboard/Items CTAs need to see.
      void qc.invalidateQueries({ queryKey: ['stats'] });
    },
  });

  const startEdit = (id: number) => {
    setEditingId(id);
  };

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-[1fr_20rem]">
      <Card>
        <CardHeader><CardTitle>{t('locations.title')}</CardTitle></CardHeader>
        <CardContent>
          {tree.isLoading && <div className="text-sm text-muted-foreground">{t('common.loading')}</div>}
          {tree.data && tree.data.length === 0 && (
            <div className="text-sm text-muted-foreground">{t('common.noData')}</div>
          )}
          {tree.data && (
            <HomeCardStrip
              nodes={tree.data}
              selectedId={selectedHomeId}
              onSelect={selectHome}
              onAddHome={() => setNewHomeOpen(true)}
            />
          )}
          {selectedHome && <RoomCardRow nodes={selectedHome.children} />}
          {selectedHome && selectedHome.children.length > 0 && (
            <div className="mb-2 flex justify-end">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={toggleAllRows}
                icon={isSubtreeFullyExpanded ? <ChevronsDownUp /> : <ChevronsUpDown />}
              >
                {isSubtreeFullyExpanded ? t('locations.collapseAll') : t('locations.expandAll')}
              </Button>
            </div>
          )}
          <ul className="space-y-1" data-testid="location-tree">
            {selectedHome && (
              <TreeRow
                key={selectedHome.id}
                node={selectedHome}
                depth={0}
                activeId={editingId}
                collapsedIds={collapsedIds}
                onToggle={toggleRow}
                onEdit={startEdit}
                onAddChild={(id) => { cancelEdit(); setForm({ ...EMPTY, parentId: id }); }}
                onDelete={(id) => remove.mutate(id)}
              />
            )}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            {editingId !== null ? t('locations.edit') : t('locations.create')}
            {editingId !== null && (
              <button type="button" onClick={cancelEdit} className="text-muted-foreground hover:text-foreground" aria-label={t('locations.cancelEdit')}>
                <X className="h-4 w-4" />
              </button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (editingId !== null) update.mutate();
              else create.mutate();
            }}
          >
            <div className="space-y-1">
              <Label htmlFor="loc-parent">{t('locations.parent')}</Label>
              <select
                id="loc-parent"
                required={editingId === null}
                value={form.parentId ?? ''}
                // No ternary to an empty/null value here: the only empty-value
                // option below is `disabled`, so a real onChange can never fire
                // with an empty string — every selectable option is a real id.
                onChange={(e) => setForm((f) => ({ ...f, parentId: Number(e.target.value) }))}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {/* No "(Kök)"/empty option — see issue #104: creating a root
                    location is only possible via the dedicated "+ Yeni Ev" flow.
                    This placeholder is unselectable and only ever shown for a
                    location that is already a root (edit mode), matching the
                    "required select with a disabled placeholder" pattern used
                    for item creation's location field. */}
                <option value="" disabled>—</option>
                {Object.entries(flatNames)
                  .filter(([id]) => !forbiddenParents.has(Number(id)))
                  .sort((a, b) => a[1].localeCompare(b[1]))
                  .map(([id, label]) => (
                    <option key={id} value={id}>{label}</option>
                  ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="loc-name">{t('locations.name')}</Label>
              <Input id="loc-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
            </div>
            <div className="space-y-1">
              <Label htmlFor="loc-type">{t('locations.type')}</Label>
              <select
                id="loc-type"
                value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as LocationType }))}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {TYPES.map((tt) => (
                  <option key={tt} value={tt}>{t(`locations.types.${tt}`)}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="loc-desc">{t('locations.description')}</Label>
              <Input id="loc-desc" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            </div>
            {editingId !== null && editing.data && (
              // Cover photo has no field of its own: it's a separate sub-resource
              // uploaded/removed immediately (see uploadCoverPhoto/removeCoverPhoto
              // above), not something the Save button commits — same as items'
              // photo sections. Only available once a location exists (edit mode),
              // matching items' own "save first, then attach photos" flow.
              <CoverPhotoSection
                location={editing.data}
                onUpload={(file) => uploadCoverPhoto.mutate(file)}
                onRemove={() => removeCoverPhoto.mutate()}
                onZoom={setZoomedCoverPhoto}
              />
            )}
            <div className="flex gap-2">
              <Button
                type="submit"
                className="flex-1"
                loading={create.isPending || update.isPending}
                // Creating requires a parent to be explicitly chosen (issue
                // #104 — no more implicit-root-via-empty-select). Editing is
                // exempt: a location that is already a root must stay
                // editable (e.g. renaming it) without being forced to pick
                // one, and forbiddenParents already keeps it from becoming
                // its own descendant.
                disabled={!form.name || (editingId === null && !form.parentId)}
                icon={editingId === null ? <Plus /> : undefined}
              >
                {editingId !== null ? t('common.save') : t('common.create')}
              </Button>
              {editingId !== null && (
                <Button type="button" variant="secondary" onClick={cancelEdit}>{t('common.cancel')}</Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
      {zoomedCoverPhoto && <PhotoLightbox src={zoomedCoverPhoto} onClose={() => setZoomedCoverPhoto(null)} />}
      <NewHomeDialog
        open={newHomeOpen}
        onOpenChange={setNewHomeOpen}
        // The tree refetch triggered by NewHomeDialog's own mutation would
        // otherwise just keep whatever was already selected (pickSelectedHomeId
        // preserves a still-valid current selection) — select the new home
        // explicitly so it's the one shown once the strip/tree update.
        onCreated={(home) => setSelectedHomeId(home.id)}
      />
    </div>
  );
}

interface CoverPhotoSectionProps {
  location: LocationView;
  onUpload: (file: File) => void;
  onRemove: () => void;
  onZoom: (url: string) => void;
}

/**
 * A location's single cover photo, edited inline in the edit panel (there's no
 * separate location detail page) — mirrors ItemDetailPage's PhotoSection
 * (upload/replace, delete, click-to-zoom via the shared PhotoLightbox), but for
 * exactly one photo instead of a gallery. Falls back to the location's type
 * icon (#73) when there's no cover photo yet. See issue #80.
 */
function CoverPhotoSection({ location, onUpload, onRemove, onZoom }: CoverPhotoSectionProps) {
  const { t } = useTranslation();
  const TypeIcon = TYPE_ICONS[location.type];
  const { coverPhotoUrl } = location;

  return (
    <div className="space-y-1">
      <Label>{t('locations.coverPhoto')}</Label>
      {coverPhotoUrl ? (
        <div className="group relative overflow-hidden rounded-md border">
          <button
            type="button"
            aria-label={t('locations.openCoverPhoto')}
            className="block w-full"
            onClick={() => onZoom(coverPhotoUrl)}
          >
            <AuthedImage src={coverPhotoUrl} className="h-32 w-full object-cover" />
          </button>
          <button
            type="button"
            className="absolute right-1 top-1 rounded bg-background/80 p-1 opacity-0 transition focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100"
            onClick={onRemove}
            aria-label={t('locations.removeCoverPhoto')}
          >
            <Trash2 className="h-3.5 w-3.5 text-destructive" />
          </button>
        </div>
      ) : (
        <div className="flex h-32 w-full items-center justify-center rounded-md border border-dashed" aria-hidden="true">
          <TypeIcon className="h-10 w-10 text-muted-foreground" />
        </div>
      )}
      <label className="flex h-10 cursor-pointer items-center justify-center gap-1.5 rounded-md border border-dashed text-sm text-muted-foreground hover:bg-accent focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
        <input
          type="file"
          accept="image/*"
          className="sr-only"
          onChange={(e) => {
            const input = e.currentTarget;
            const f = input.files?.[0];
            if (f) onUpload(f);
            // Reset so picking the same file again still fires change.
            input.value = '';
          }}
        />
        <Upload className="h-4 w-4" />
        {coverPhotoUrl ? t('locations.replaceCoverPhoto') : t('locations.addCoverPhoto')}
      </label>
    </div>
  );
}

interface LocationCardBodyProps {
  node: LocationNode;
}

/**
 * Icon/cover-photo + name + rolled-up item count — the visual content shared
 * by both card rows below (the home strip and the selected home's room row),
 * so the two stay in sync automatically instead of duplicating markup. See
 * issues #76, #73, #80, #100.
 */
function LocationCardBody({ node }: LocationCardBodyProps) {
  const { t } = useTranslation();
  const TypeIcon = TYPE_ICONS[node.type];
  return (
    <>
      {node.coverPhotoUrl ? (
        <AuthedImage src={node.coverPhotoUrl} className="h-10 w-10 rounded-md object-cover" />
      ) : (
        <TypeIcon className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
      )}
      <span className="w-full truncate text-sm font-medium" title={node.name}>
        {node.name}
      </span>
      <span className="text-xs text-muted-foreground">
        {t('locations.itemCount', { count: node.totalItemCount })}
      </span>
    </>
  );
}

interface HomeCardStripProps {
  nodes: LocationNode[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  /** Opens the "create a new home" dialog — see NewHomeDialog and issue #104. */
  onAddHome: () => void;
}

/**
 * Horizontal, scrollable strip of cards for the root (top-level) locations —
 * "homes". Originally added (issue #76) as a quick-access complement to the
 * full tree below; reworked in #100 after the owner found the label wrong
 * (these are homes, not rooms) and the content duplicated between the strip
 * and the tree. Now:
 *  - clicking a card selects that home for the rest of the page instead of
 *    navigating to its items (see RoomCardRow / the tree below, which scope
 *    to whichever home is selected here) — this same click also persists the
 *    home as the device-local default (see selectHome in LocationsPage), so
 *    it's auto-selected again next time (src/lib/defaultHome.ts). A separate
 *    "set default" radio used to sit on each card for this but was dropped
 *    (issue #126) since the click already did the job;
 *  - a trailing dashed "+ Yeni Ev" card opens NewHomeDialog (issue #104) —
 *    reuses the same dashed-empty-state visual language as issue #74's
 *    empty-location styling instead of introducing a new pattern;
 *  - always renders *something*, even for a brand-new account with zero
 *    homes: instead of the card list, a standalone "Ev Ekle" button takes
 *    its place, so the entry point to create a first home is never just
 *    missing (issue #104).
 *
 * Deliberately does not filter by `type`: root locations aren't guaranteed to
 * be HOME (a ROOM or OTHER location can just as well sit at the top level),
 * so this mirrors whatever the tree's top-level array contains, unfiltered.
 */
function HomeCardStrip({ nodes, selectedId, onSelect, onAddHome }: HomeCardStripProps) {
  const { t } = useTranslation();

  if (nodes.length === 0) {
    return (
      <div className="mb-4 border-b border-border pb-4" data-testid="home-card-strip">
        <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t('locations.homeStripTitle')}
        </h4>
        <Button type="button" variant="secondary" size="sm" onClick={onAddHome} icon={<Plus />}>
          {t('locations.addHomeCta')}
        </Button>
      </div>
    );
  }

  return (
    <div className="mb-4 border-b border-border pb-4" data-testid="home-card-strip">
      <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {t('locations.homeStripTitle')}
      </h4>
      <ul className="flex gap-3 overflow-x-auto pb-1">
        {nodes.map((node) => {
          const isSelected = node.id === selectedId;
          return (
            <li key={node.id} className="shrink-0">
              <button
                type="button"
                aria-current={isSelected ? 'true' : undefined}
                onClick={() => onSelect(node.id)}
                className={`flex w-28 flex-col items-center gap-1.5 rounded-lg border bg-card p-3 text-center transition-colors hover:bg-accent hover:shadow-sm sm:w-32 ${
                  isSelected ? 'border-brand-600 bg-brand-50 dark:bg-brand-500/15' : ''
                }`}
              >
                <LocationCardBody node={node} />
              </button>
            </li>
          );
        })}
        <li className="shrink-0">
          <button
            type="button"
            onClick={onAddHome}
            className="flex h-full w-28 flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed p-3 text-center text-muted-foreground transition-colors hover:bg-accent hover:text-foreground sm:w-32"
          >
            <Plus className="h-6 w-6" aria-hidden="true" />
            <span className="text-sm font-medium">{t('locations.newHome')}</span>
          </button>
        </li>
      </ul>
    </div>
  );
}

interface RoomCardRowProps {
  nodes: LocationNode[];
}

/**
 * Second-level card row: the direct children ("rooms") of whichever home is
 * currently selected in HomeCardStrip above. Reuses the same LocationCardBody
 * so it looks identical to the home strip, but — unlike the home cards —
 * clicking one still opens that room's items directly, unchanged from #76's
 * original click behavior. See issue #100 requirements 5 and 7.
 */
function RoomCardRow({ nodes }: RoomCardRowProps) {
  const { t } = useTranslation();
  if (nodes.length === 0) return null;

  return (
    <div className="mb-4 border-b border-border pb-4" data-testid="room-card-row">
      <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {t('locations.roomStripTitle')}
      </h4>
      <ul className="flex gap-3 overflow-x-auto pb-1">
        {nodes.map((node) => (
          <li key={node.id} className="shrink-0">
            <RouterLink
              to={`/items?locationId=${node.id}`}
              className="flex w-28 flex-col items-center gap-1.5 rounded-lg border bg-card p-3 text-center transition-colors hover:bg-accent hover:shadow-sm sm:w-32"
            >
              <LocationCardBody node={node} />
            </RouterLink>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface RowProps {
  node: LocationNode;
  depth: number;
  activeId: number | null;
  // Expand/collapse state lifted to LocationsPage (issue #101) so the
  // "expand/collapse all" button can flip every row at once; individual
  // chevron clicks still work via onToggle, unchanged from the reader's
  // perspective.
  collapsedIds: Set<number>;
  onToggle: (id: number) => void;
  onEdit: (id: number) => void;
  onAddChild: (id: number) => void;
  onDelete: (id: number) => void;
}

function TreeRow({ node, depth, activeId, collapsedIds, onToggle, onEdit, onAddChild, onDelete }: RowProps) {
  const open = !collapsedIds.has(node.id);
  const { t } = useTranslation();
  const isActive = activeId === node.id;
  const TypeIcon = TYPE_ICONS[node.type];
  // itemCount is this location's own items only — deliberately not totalItemCount, so a
  // parent that only holds items via a child (e.g. "Dolap" whose items all live in a
  // "Çekmece") still gets the empty-state treatment for itself. See issue #74.
  const isEmpty = node.itemCount === 0;
  return (
    <li>
      <div
        aria-current={isActive ? 'true' : undefined}
        className={`group flex items-center gap-2 rounded-md border-l-[3px] py-1 pl-1 pr-2 hover:bg-accent ${
          isActive ? 'border-brand-600 bg-brand-50 dark:bg-brand-500/15' : 'border-transparent'
        } ${isEmpty ? 'border-y border-r border-dashed' : ''}`}
        style={{ paddingLeft: `${depth * 16 + 4}px` }}
      >
        <button
          type="button"
          aria-expanded={node.children.length > 0 ? open : undefined}
          aria-label={t(open ? 'locations.collapse' : 'locations.expand', { name: node.name })}
          className="text-muted-foreground"
          onClick={() => onToggle(node.id)}
          disabled={node.children.length === 0}
        >
          {node.children.length === 0 ? <span className="inline-block w-4" /> : open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        {node.coverPhotoUrl ? (
          <AuthedImage src={node.coverPhotoUrl} className="h-5 w-5 shrink-0 rounded object-cover" />
        ) : (
          <TypeIcon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        )}
        <Link
          to={`/items?locationId=${node.id}`}
          intent="standalone"
          className={`min-w-0 flex-1 min-h-0 truncate px-1 py-0.5 text-left text-sm ${isActive ? 'text-brand-700 font-semibold' : ''}`}
        >
          <span className="font-medium">{node.name}</span>
          <span className="ml-2 text-xs text-muted-foreground">
            {t(`locations.types.${node.type}`)} · {t('locations.itemCount', { count: node.totalItemCount })}
          </span>
        </Link>
        <div className="flex items-center gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
          {isEmpty && (
            <RouterLink
              to={`/items/new?locationId=${node.id}`}
              className="mr-1 inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md px-1.5 py-0.5 text-xs font-medium text-brand-700 hover:bg-brand-500/10 hover:underline dark:text-brand-300"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              {t('locations.addItem')}
            </RouterLink>
          )}
          <button type="button" aria-label={t('locations.addChild')} onClick={() => onAddChild(node.id)} title={t('locations.addChild')}>
            <Plus className="h-4 w-4 text-muted-foreground hover:text-primary" />
          </button>
          <button type="button" aria-label={t('common.edit')} onClick={() => onEdit(node.id)} title={t('common.edit')}>
            <Pencil className="h-4 w-4 text-muted-foreground hover:text-primary" />
          </button>
          <button
            type="button"
            aria-label={t('common.delete')}
            onClick={() => { if (window.confirm(t('locations.deleteConfirm'))) onDelete(node.id); }}
            title={t('common.delete')}
          >
            <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
          </button>
        </div>
      </div>
      {open && node.children.length > 0 && (
        <ul>
          {node.children.map((c) => (
            <TreeRow
              key={c.id}
              node={c}
              depth={depth + 1}
              activeId={activeId}
              collapsedIds={collapsedIds}
              onToggle={onToggle}
              onEdit={onEdit}
              onAddChild={onAddChild}
              onDelete={onDelete}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
